import { useState, useRef, useEffect } from "react";
import { FadeIn } from "@/components/common/FadeIn";
import { Building2, CircleDollarSign, Users, Truck, Factory, Ship, Database } from "lucide-react";

const panelImages = {
  finanzas: { src: "/soluciones-finanzas-detalle.jpg", alt: "Detalle de módulo Finanzas" },
  logistica: { src: "/soluciones-logistica-detalle.jpg", alt: "Detalle de módulo Logística" },
  comercio: { src: "/soluciones-comext-detalle.jpg", alt: "Detalle de módulo Comercio Exterior" },
  produccion: { src: "/soluciones-produccion-detalle.jpg", alt: "Detalle de módulo Producción" },
  rrhh: { src: "/soluciones-rrhh-detalle.jpg", alt: "Detalle de módulo Recursos Humanos" },
  administracion: { src: "/soluciones-administracion-detalle.jpg", alt: "Detalle de módulo Administración" },
};

const solutionNodes = [
  { id: "administracion", label: "Administración", angle: 300, Icon: Building2 },
  { id: "finanzas", label: "Finanzas", angle: 0, Icon: CircleDollarSign },
  { id: "logistica", label: "Logística", angle: 60, Icon: Truck },
  { id: "comercio", label: "Comercio Exterior", angle: 120, Icon: Ship },
  { id: "produccion", label: "Producción", angle: 180, Icon: Factory },
  { id: "rrhh", label: "Recursos Humanos", angle: 240, Icon: Users },
];

const ORBIT = { centerX: 550, centerY: 380, radius: 230, iconRadius: 70 };

const getNodePoint = (angle, radius = ORBIT.radius) => {
  const radians = (angle * Math.PI) / 180;
  return {
    x: ORBIT.centerX + Math.sin(radians) * radius,
    y: ORBIT.centerY - Math.cos(radians) * radius,
  };
};

export const Solutions = () => {
  const [activePanel, setActivePanel] = useState(null);
  const sectionRef = useRef(null);
  const panelRef = useRef(null);
  const desktopNodes = solutionNodes.map((node) => ({ ...node, ...getNodePoint(node.angle) }));

  useEffect(() => {
    if (activePanel && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activePanel]);

  const togglePanel = (id) => setActivePanel((prev) => (prev === id ? null : id));

  const closePanel = () => {
    setActivePanel(null);
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="soluciones" ref={sectionRef} className="py-20 md:py-28" data-testid="solutions-section">
      <div className="section-container">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="solutions-kicker-text">
            Soluciones
          </p>
          <h2
            className="corporate-title-balance mt-3 text-2xl md:text-3xl text-[#005F92]"
            data-testid="solutions-heading-text"
          >
            Áreas integradas del <span className="solutions-eras-brand">ERP PRAGMATIC XXI</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.1}>
          <article
            className="relative mt-10 overflow-hidden rounded-2xl border border-[#BCD5EA] bg-gradient-to-b from-[#F2F8FF] to-[#E3F0FA] p-4 md:p-6"
            data-testid="solutions-image-card"
          >
            <div className="relative md:hidden" data-testid="solutions-mobile-cloud-wrap">
              <img
                src="/solutions-cloud-outline-thin-hq.png"
                alt="Marco de nube"
                className="pointer-events-none absolute inset-0 h-full w-full object-fill opacity-65 blur-[0.3px] contrast-95"
                data-testid="solutions-mobile-cloud-frame"
              />

              <div className="mb-6 flex flex-col items-center" data-testid="solutions-mobile-center-node">
                <div className="relative h-24 w-24" data-testid="solutions-mobile-center-icon-wrap">
                  <Database className="h-full w-full text-[#005F92]" strokeWidth={1.55} data-testid="solutions-mobile-center-database-icon" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-testid="solutions-mobile-grid">
                {solutionNodes.map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => togglePanel(node.id)}
                    className="rounded-xl border border-[#74AFC8] bg-white/95 p-3"
                    data-testid={`solutions-mobile-node-${node.id}-button`}
                  >
                    <div className="relative mx-auto flex h-20 w-20 items-center justify-center" data-testid={`solutions-mobile-node-image-frame-${node.id}`}>
                      <span className="absolute inset-0 rounded-full border border-[#74AFC8]" />
                      <span className="absolute inset-[8px] rounded-full border border-[#C4DBEC]" />
                      <node.Icon
                        className="h-[58px] w-[58px] text-[#1B6A97]"
                        strokeWidth={1.3}
                        data-testid={`solutions-mobile-node-icon-${node.id}`}
                      />
                    </div>
                    <p
                      className="corporate-title-balance mt-3 text-center text-sm font-bold text-[#003E69] [text-shadow:0_1px_0_rgba(255,255,255,0.85)]"
                      data-testid={`solutions-mobile-node-title-${node.id}`}
                    >
                      {node.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="relative mx-auto hidden h-[760px] max-w-[1100px] md:block" data-testid="solutions-orbit-desktop">
              <img
                src="/solutions-cloud-outline-thin-hq.png"
                alt="Marco de nube"
                className="pointer-events-none absolute inset-0 h-full w-full object-fill opacity-65 blur-[0.3px] contrast-95"
                data-testid="solutions-desktop-cloud-frame"
              />

              <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-[#7FB5CF]/80"
                style={{ width: `${ORBIT.radius * 2 + 40}px`, height: `${ORBIT.radius * 2 + 40}px` }}
                data-testid="solutions-systems-orbit-ring"
              />

              <svg
                className="pointer-events-none absolute inset-0"
                viewBox="0 0 1100 760"
                data-testid="solutions-connections-svg"
              >
                <defs>
                  <marker id="arrow-bidirectional" markerWidth="12" markerHeight="12" refX="6" refY="6" orient="auto">
                    <path d="M1,1 L11,6 L1,11 L4,6 z" fill="#3E93BE" />
                  </marker>
                </defs>

                {desktopNodes.map((node, index) => {
                  const next = desktopNodes[(index + 1) % desktopNodes.length];
                  const connectorRadius = ORBIT.radius + 62;
                  const trimDegrees = 12;
                  const startAngle = (node.angle + trimDegrees) % 360;
                  const endAngle = (next.angle - trimDegrees + 360) % 360;
                  const start = getNodePoint(startAngle, connectorRadius);
                  const end = getNodePoint(endAngle, connectorRadius);

                  return (
                    <path
                      key={`${node.id}-to-${next.id}`}
                      d={`M ${start.x} ${start.y} A ${connectorRadius} ${connectorRadius} 0 0 1 ${end.x} ${end.y}`}
                      fill="none"
                      stroke="#3E93BE"
                      strokeWidth="1.55"
                      markerStart="url(#arrow-bidirectional)"
                      markerEnd="url(#arrow-bidirectional)"
                      data-testid={`solutions-connector-${node.id}-to-${next.id}`}
                    />
                  );
                })}
              </svg>

              <article className="absolute left-1/2 top-1/2 w-[260px] -translate-x-1/2 -translate-y-1/2" data-testid="solutions-center-node">
                <div className="relative mx-auto flex h-[170px] w-[170px] items-center justify-center" data-testid="solutions-center-node-image">
                  <span className="absolute inset-0 rounded-full border border-[#8AB7D1]" />
                  <span className="absolute inset-[14px] rounded-full border border-[#BAD6E8]" />
                  <Database className="h-[128px] w-[128px] text-[#005F92]" strokeWidth={1.35} data-testid="solutions-center-database-icon" />
                </div>
              </article>

              {desktopNodes.map((node) => (
                <button
                  key={node.id}
                  type="button"
                  onClick={() => togglePanel(node.id)}
                  className="absolute w-[220px] -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${node.x}px`, top: `${node.y}px` }}
                  data-testid={`solutions-orbit-node-${node.id}-button`}
                >
                  <div className="relative mx-auto flex h-[128px] w-[128px] items-center justify-center" data-testid={`solutions-orbit-node-image-${node.id}`}>
                    <span className="absolute inset-0 rounded-full border border-[#74AFC8]" />
                    <span className="absolute inset-[12px] rounded-full border border-[#C7DEED]" />
                    <node.Icon
                      className="h-[84px] w-[84px] text-[#1B6A97]"
                      strokeWidth={1.25}
                      data-testid={`solutions-orbit-node-icon-${node.id}`}
                    />
                  </div>
                  <div className="mt-4 text-center" data-testid={`solutions-orbit-node-title-wrap-${node.id}`}>
                    <p className="corporate-title-balance inline-block rounded-full bg-white/90 px-3 py-1 text-sm font-bold uppercase tracking-[0.06em] text-[#003E69] [text-shadow:0_1px_0_rgba(255,255,255,0.85)]" data-testid={`solutions-orbit-node-title-${node.id}`}>
                      {node.label}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div
              className="absolute bottom-4 right-6 z-20 w-fit"
              data-testid="solutions-signature-block"
            >
              <img
                src="/logo-erp-isotipo.png"
                alt="Firma con logo isotipo Pragmatic XXI"
                className="h-[88px] w-[300px] object-contain object-center"
                data-testid="solutions-signature-logo"
              />
            </div>
          </article>

          {activePanel && panelImages[activePanel] && (
            <article
              ref={panelRef}
              className="mt-6 overflow-hidden rounded-2xl border border-[#9BC4DC] bg-white"
              data-testid={`solutions-${activePanel}-expanded-panel`}
            >
              <button
                type="button"
                onClick={closePanel}
                className="w-full"
                data-testid={`solutions-${activePanel}-close-image-button`}
              >
                <img
                  src={panelImages[activePanel].src}
                  alt={panelImages[activePanel].alt}
                  className="h-auto w-full object-contain"
                  data-testid={`solutions-${activePanel}-expanded-image`}
                />
              </button>
            </article>
          )}
        </FadeIn>
      </div>
    </section>
  );
};
